import React from 'react';
import { FieldValues } from 'react-hook-form';

import SortDown from '@/assets/icons/SortDown';
import SortUp from '@/assets/icons/SortUp';
import { SorteringRichting, SorteringType, SorteringTypeLabel } from '@/components/sidebar/sortField/useSorting';
import { SelectField } from '@/components/UI/Fields/ReactFormFields/SelectField';
import { FieldProps } from '@/components/UI/Fields/types';

import styles from './SortField.module.css';

type SortFieldProps<T extends FieldValues> = FieldProps<T> & {
  options?: { label: SorteringTypeLabel; value: SorteringType }[];
  onChangeCallback?: (p: SorteringType) => void;
  defaultRichting?: SorteringRichting;
  onSortDirectionChangeCallback?: (p: SorteringRichting) => void;
  selectedSortDirection: SorteringRichting;
  hasSeparationLine?: boolean;
} & Omit<React.ComponentPropsWithoutRef<typeof SelectField>, keyof FieldProps<T>>;

const SortField = <T extends FieldValues>({
  name,
  label = 'Sorteren',
  testId = 'sort-field',
  placeholder = 'Sorteren op...',
  control,
  defaultValue,
  onChangeCallback,
  onSortDirectionChangeCallback,
  selectedSortDirection,
  options,
  required = true,
  hasSeparationLine = true,
  ...rest
}: SortFieldProps<T>) => {
  const sortDirectionIcon = selectedSortDirection === SorteringRichting.UP ? 'SortUp' : 'SortDown';

  const toggleSortDirection = () => {
    if (onSortDirectionChangeCallback) {
      onSortDirectionChangeCallback(sortDirectionIcon === 'SortDown' ? SorteringRichting.UP : SorteringRichting.DOWN);
    }
  };

  return (
    <>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '100%' }}>
          <SelectField
            placeholder={placeholder}
            control={control}
            onChangeCallback={onChangeCallback}
            required={required}
            testId={`${testId}-sort-select`}
            name={name}
            label={label}
            defaultValue={defaultValue}
            options={options}
            {...rest}
          />
        </div>
        <div className={styles.sortDirectionButtonStyle}>
          {sortDirectionIcon === 'SortDown' ? (
            <SortDown
              data-testid={`${testId}-sort-button`}
              data-icon='SortDown'
              fill='var(--tractie-color-content-interactive-default)'
              onClick={toggleSortDirection}
              width='24px'
              height='24px'
              aria-hidden='true'
            />
          ) : (
            <SortUp
              data-testid={`${testId}-sort-button`}
              data-icon='SortUp'
              fill='var(--tractie-color-content-interactive-default)'
              onClick={toggleSortDirection}
              width='24px'
              height='24px'
              aria-hidden='true'
            />
          )}
        </div>
      </div>
      {hasSeparationLine && (
        <hr style={{ color: 'var(--tractie-color-border-default)', marginBottom: '24px', marginTop: '12px' }} />
      )}
    </>
  );
};

export default SortField;
